import { useMemo } from 'react'
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import useLocalStorage from '@/hooks/useLocalStorage'

const workflowTabs: Record<string, string> & { settings?: string } = {
  metrics: 'Metrics',
  documents: 'Documents',
  data: 'Data',
  settings: 'Settings',
}

const sortColumns = {
  enteredAtColId: 'State Duration',
  uploadColId: 'Upload Date',
}

const sortOrder = {
  desc: 'Newest at Top',
  asc: 'Oldest at Top',
}

const autoAssignCardVisibilityOptions = {
  show: 'Show',
  hide: 'Hide',
}

type UserPreferenceProps = {
  label: string
  description: string
  optionsMap: Record<string, string>
  value: string
  setValue: (value: string) => void
}

function UserPreference({
  label,
  description,
  value,
  optionsMap,
  setValue,
}: UserPreferenceProps) {
  return (
    <Stack spacing={2}>
      <div>
        <Typography variant="body1">{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </div>
      <RadioGroup
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
        }}
        sx={{ ml: '5px !important' }}
      >
        {Object.entries(optionsMap).map(([key, value]) => (
          <FormControlLabel
            key={key}
            value={key}
            control={
              <Radio color="neutral" sx={{ padding: 0.5 }} size="small" />
            }
            label={value}
            sx={{
              columnGap: 0.5,
              width: 'fit-content',
            }}
          />
        ))}
      </RadioGroup>
    </Stack>
  )
}

export default function UserPreferences() {
  const featureFlags = useFeatureFlagContext()
  const { isAuthorized } = useStytchIsAuthorized('pd.project', 'edit')

  const [projectPreferredTab, setProjectPreferredTab] = useLocalStorage(
    'project-preferred-tab',
    'documents',
  )

  const [projDefaultSortColumn, setProjDefaultSortColumn] = useLocalStorage(
    'proj-default-sort-columns',
    'enteredAtColId',
  )

  const [projDefaultSortOrder, setProjDefaultSortOrder] = useLocalStorage(
    'proj-default-sort-order',
    'desc',
  )

  const [autoAssignCardVisibility, setAutoAssignCardVisibility] =
    useLocalStorage('auto-assign-card-visibility', 'show')

  const defaultTabsOptions = useMemo(() => {
    if (isAuthorized) return workflowTabs
    const tempTabs = { ...workflowTabs }
    delete tempTabs['settings']
    return tempTabs
  }, [isAuthorized])

  return (
    <Stack spacing={4} sx={{ height: '100%' }}>
      <Typography variant="h4">My Preferences</Typography>
      <UserPreference
        label="Default Workflow Tab"
        description="Which tab is selected by default when entering a project."
        value={
          // For legacy reasons, the value was set to "dashboard" but the tab was renamed to "metrics"
          projectPreferredTab === 'dashboard' ? 'metrics' : projectPreferredTab
        }
        optionsMap={defaultTabsOptions}
        setValue={setProjectPreferredTab}
      />
      <UserPreference
        label="Default Sort Column"
        description="On the Documents and Data tabs in a project, rows will be sorted by this column when the page first loads."
        value={projDefaultSortColumn}
        optionsMap={sortColumns}
        setValue={setProjDefaultSortColumn}
      />
      <UserPreference
        label="Default Sort Order"
        description="On the Documents and Data tabs in a project, rows will be sorted in this order when the page first loads."
        value={projDefaultSortOrder}
        optionsMap={sortOrder}
        setValue={setProjDefaultSortOrder}
      />
      {featureFlags.auto_sort_documents_card && (
        <UserPreference
          label="Auto Assign Card Visibility"
          description="Visibility of the auto sort card on the projects page."
          value={autoAssignCardVisibility}
          optionsMap={autoAssignCardVisibilityOptions}
          setValue={setAutoAssignCardVisibility}
        />
      )}
    </Stack>
  )
}
